<script setup lang="ts">
import { ref, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { Server, ShieldCheck, Zap, ArrowRight, Lock, User, Eye, EyeOff, Radio, Cpu, Network } from 'lucide-vue-next'
import { ElMessage } from 'element-plus'
import { useUserStore } from '../stores/user'
import { fetchPublicKey, encryptPassword } from '../utils/crypto'

const router = useRouter()
const userStore = useUserStore()
const loading = ref(false)
const showPassword = ref(false)
const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '')
const basePath = (import.meta.env.VITE_API_BASE_PATH || '').replace(/\/$/, '')

// 表单数据
const passwordForm = reactive({
  username: '',
  password: ''
})

// 账号密码登录
const handlePasswordLogin = async () => {
  if (!passwordForm.username || !passwordForm.password) {
    ElMessage.warning('请输入账号和密码')
    return
  }

  loading.value = true
  try {
    const publicKey = await fetchPublicKey(apiBaseUrl, basePath)
    const encryptedPwd = encryptPassword(passwordForm.password, publicKey)

    const response = await fetch(`${apiBaseUrl}${basePath}/auth/login-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: passwordForm.username, password: encryptedPwd })
    })
    const data = await response.json()
    if (data.code === 200 && data.data) {
      userStore.setToken(data.data.token)
      router.push('/gateway-orders')
    } else {
      ElMessage.error(data.message || '登录失败')
    }
  } catch (error) {
    console.error('Password login error:', error)
    ElMessage.error('登录失败，请稍后重试')
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="min-h-screen w-full flex items-center justify-center bg-slate-900 px-4 py-12 font-sans relative">
    <div class="w-full max-w-md bg-slate-850 rounded-2xl border border-slate-800 p-8 shadow-2xl">
      <!-- 标头 -->
      <div class="flex flex-col items-center text-center mb-8">
        <div class="w-12 h-12 rounded-xl bg-cyan-600 flex items-center justify-center shadow-md mb-4">
          <Zap class="w-6 h-6 text-white" />
        </div>
        <div class="flex items-center gap-2">
          <h1 class="text-xl font-bold text-white tracking-wide">JDY</h1>
          <span class="text-xs px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400 font-mono font-medium">GATEWAY</span>
        </div>
        <p class="text-xs text-slate-400 mt-1">支付网关管理控制台</p>
      </div>

      <!-- 登录表单 -->
      <form @submit.prevent="handlePasswordLogin" class="space-y-4">
        <!-- 账号输入 -->
        <div>
          <label class="block text-xs font-medium text-slate-300 mb-1.5">
            账号
          </label>
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-500">
              <User class="w-4 h-4" />
            </div>
            <input
              v-model="passwordForm.username"
              type="text"
              autocomplete="username"
              placeholder="请输入管理员账号"
              class="w-full bg-slate-800 border border-slate-700 text-slate-100 placeholder-slate-500 text-sm pl-10 pr-4 py-2.5 rounded-lg focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
            />
          </div>
        </div>

        <!-- 密码输入 -->
        <div>
          <label class="block text-xs font-medium text-slate-300 mb-1.5">
            密码
          </label>
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-500">
              <Lock class="w-4 h-4" />
            </div>
            <input
              v-model="passwordForm.password"
              :type="showPassword ? 'text' : 'password'"
              autocomplete="current-password"
              placeholder="请输入登录密码"
              class="w-full bg-slate-800 border border-slate-700 text-slate-100 placeholder-slate-500 text-sm pl-10 pr-10 py-2.5 rounded-lg focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
            />
            <button
              type="button"
              @click="showPassword = !showPassword"
              class="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-500 hover:text-slate-300 transition-colors"
              tabindex="-1"
            >
              <Eye v-if="showPassword" class="w-4 h-4" />
              <EyeOff v-else class="w-4 h-4" />
            </button>
          </div>
        </div>

        <!-- 提交按钮 -->
        <button
          type="submit"
          :disabled="loading"
          class="w-full mt-6 bg-cyan-600 hover:bg-cyan-500 text-white font-medium py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:pointer-events-none text-sm"
        >
          <span v-if="loading" class="flex items-center gap-2">
            <svg class="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            登录中...
          </span>
          <span v-else class="flex items-center gap-2">
            登录
            <ArrowRight class="w-4 h-4" />
          </span>
        </button>
      </form>

      <!-- 底部版权 -->
      <div class="mt-8 pt-4 border-t border-slate-800 text-center text-xs text-slate-500">
        © 2026 JDY PAY 网关控制台
      </div>
    </div>
  </div>
</template>
